import { create } from 'zustand';
import ServiceAfastamento from '@/services/ServiceAfastamento';
import { notify } from '@/stores/useToast';
import {
  buildChartVM,
  buildRequestBody,
  type AfastamentoFiltros,
  type ChartVM,
  type DashboardDTO,
} from '@/lib/afastamento';

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (history/active-and-retired?sort=<alias>,<asc|desc>). Só as colunas com alias entram
// como sortable — as demais não têm suporte de ordenação no BFF.
const SORT_ALIASES: Record<string, string> = {
  empresa: 'nomeEmpresa',
  cpf: 'cpf',
  matricula: 'matricula',
  motivo: 'motivoAfastamento',
  inicio: 'inicioAfastamento',
  fim: 'fimAfastamento',
  grupoCategoria: 'grupoCategoria',
  categoria: 'categoria',
  regime: 'tpRegPrev',
  cbo: 'cbo',
  cargo: 'cargo',
  situacao: 'situacao',
  idDocumento: 'idDocumento',
};

// situação (código do BFF) -> rótulo PT-BR exibido na tabela.
export const SITUACAO_LABEL: Record<string, string> = {
  retired: 'Afastado',
  inactive: 'Inativo',
  vacation: 'Descanso remunerado',
  unknown: '-',
};

export interface LinhaAfastamento {
  [k: string]: unknown;
  _key: string;
  empresa: string;
  cnpj: string;
  cpf: string;
  matricula: string;
  motivo: string;
  inicio: string;
  fim: string | null;
  grupoCategoria: string;
  categoria: string;
  regime: string;
  cbo: string;
  cargo: string;
  situacao: string;
  idDocumento: string;
}

export interface OpcoesFiltro {
  cnpj: { value: string; label: string }[];
  regime: { value: string; label: string }[];
  categoria: { value: string; label: string }[];
  motivo: { value: string; label: string }[];
  cargo: { value: string; label: string }[];
  cbo: { value: string; label: string }[];
}

const FILTROS_VAZIO: AfastamentoFiltros = {
  cnpjList: [],
  startDate: undefined,
  endDate: undefined,
  regimeList: [],
  categoriaList: [],
  motivoList: [],
  cargoList: [],
  cboList: [],
};

const OPCOES_VAZIO: OpcoesFiltro = {
  cnpj: [],
  regime: [],
  categoria: [],
  motivo: [],
  cargo: [],
  cbo: [],
};

type Any = any;

const normalizar = (
  h: Any,
  i: number,
): {
  _key: string;
  empresa: any;
  cnpj: any;
  cpf: any;
  matricula: any;
  motivo: any;
  inicio: any;
  fim: null;
  grupoCategoria: any;
  categoria: any;
  regime: any;
  cbo: any;
  cargo: any;
  situacao: string;
  idDocumento: any;
} => ({
  _key: `${h.idDocumento ?? 'row'}-${i}`,
  empresa: h.nomeEmpresa ?? '',
  cnpj: h.cnpj ?? '',
  cpf: h.cpf ?? '',
  matricula: h.matricula ?? '',
  motivo: h.motivoAfastamento ?? '',
  inicio: h.inicioAfastamento ?? '',
  fim: h.fimAfastamento ?? null,
  grupoCategoria: h.grupoCategoria ?? '',
  categoria: h.categoria ?? '',
  regime: h.tpRegPrev ?? '',
  cbo: h.cbo ?? '',
  cargo: h.cargo ?? '',
  situacao: SITUACAO_LABEL[h.situacao as string] ?? h.situacao ?? '',
  idDocumento: h.idDocumento ?? '',
});

interface State {
  dashboard: DashboardDTO | null;
  vm: ChartVM;
  lista: LinhaAfastamento[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  afastados: boolean;
  inativos: boolean;
  descanso: boolean;
  semMotivo: boolean;
  busca: string;
  filtros: AfastamentoFiltros;
  opcoes: OpcoesFiltro;
  carregandoGraficos: boolean;
  carregandoLista: boolean;
  exportando: boolean;
  carregarDashboard: () => Promise<void>;
  carregarHistorico: () => Promise<void>;
  carregarFiltros: () => Promise<void>;
  aplicarFiltros: (f: AfastamentoFiltros) => Promise<void>;
  setToggle: (k: 'afastados' | 'inativos' | 'descanso' | 'semMotivo', v: boolean) => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  exportar: () => Promise<void>;
}

function _body(get: () => State) {
  const s = get();
  return buildRequestBody({
    afastados: s.afastados,
    inativos: s.inativos,
    descanso: s.descanso,
    semMotivo: s.semMotivo,
    busca: s.busca,
    filtros: s.filtros,
  });
}

export const useAfastamentoStore = create<State>((set, get) => ({
  dashboard: null,
  vm: buildChartVM(undefined),
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc',
  afastados: true,
  inativos: true,
  descanso: true,
  semMotivo: false,
  busca: '',
  filtros: { ...FILTROS_VAZIO },
  opcoes: { ...OPCOES_VAZIO },
  carregandoGraficos: false,
  carregandoLista: false,
  exportando: false,

  async carregarDashboard() {
    set({ carregandoGraficos: true });
    try {
      const { data } = await ServiceAfastamento.dashboard(_body(get));
      set({ dashboard: data, vm: buildChartVM(data) });
    } catch {
      set({ dashboard: null, vm: buildChartVM(undefined) });
      notify.error('Erro ao carregar os gráficos de afastamento.');
    } finally {
      set({ carregandoGraficos: false });
    }
  },

  async carregarHistorico() {
    const { page, size, sortBy, sortOrder } = get();
    set({ carregandoLista: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const { data } = await ServiceAfastamento.history(_body(get), {
        page,
        size,
        sort: alias ? `${alias},${sortOrder}` : undefined,
      });
      set({
        lista: (data.content ?? []).map(normalizar),
        total: data.totalElements ?? 0,
      });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar os afastamentos.');
    } finally {
      set({ carregandoLista: false });
    }
  },

  async carregarFiltros() {
    try {
      const { data } = await ServiceAfastamento.filters();
      const opt = (arr: Any[]) => (arr ?? []).map((i) => ({ value: i.itemId, label: i.itemTitle }));
      set({
        opcoes: {
          // CNPJ não vem deste endpoint (origin/destination são de cessão); fica vazio.
          cnpj: [],
          regime: opt(data.groupRegimesPrevidenciarios),
          categoria: opt(data.groupCategoria),
          motivo: opt(data.groupTabelaMotivosAfastamento),
          cargo: opt(data.groupCargoList),
          cbo: opt(data.groupCboCodeNmCargoList),
        },
      });
    } catch {
      notify.error('Erro ao carregar as opções de filtro.');
    }
  },

  async aplicarFiltros(f) {
    set({ filtros: f, page: 0 });
    const requests = [get().carregarHistorico()];
    if (!get().semMotivo) requests.push(get().carregarDashboard());
    await Promise.all(requests);
  },

  async setToggle(k, v) {
    set({ [k]: v, page: 0 } as Partial<State>);
    const requests = [get().carregarHistorico()];
    if (!get().semMotivo) requests.push(get().carregarDashboard());
    await Promise.all(requests);
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregarHistorico();
  },

  async setPage(page) {
    set({ page });
    await get().carregarHistorico();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregarHistorico();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregarHistorico();
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceAfastamento.exportData(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar os afastamentos.');
    } finally {
      set({ exportando: false });
    }
  },
}));
